import { Injectable } from '@angular/core';
import { AuthErrorCode } from '@app/types/auth.interface';
import { AUTH_CONFIG } from '@app/core/config/auth.config';

/**
 * Servicio para manejar notificaciones relacionadas con autenticación.
 *
 * NOTA: Esta es una implementación básica con console.log.
 * Para producción, puedes integrar este servicio con tu sistema de notificaciones
 * existente (toast, snackbar, alert, etc.)
 *
 * Ejemplos de integración:
 * - Angular Material Snackbar: this.snackBar.open(message, 'Cerrar', { duration: 3000 })
 * - PrimeNG Toast: this.messageService.add({ severity: 'error', summary: 'Error', detail: message })
 * - Notyf: this.notyf.error(message)
 */
@Injectable({
  providedIn: 'root',
})
export class AuthNotificationService {
  /**
   * Muestra una notificación de error basada en el código de error
   */
  showAuthError(errorCode: AuthErrorCode): void {
    const message = this.getErrorMessage(errorCode);

    switch (errorCode) {
      case AUTH_CONFIG.ERROR_CODES.TOKEN_EXPIRED:
        // Para token expirado, mostrar mensaje informativo (no es un error grave)
        this.showInfo(message);
        break;

      case AUTH_CONFIG.ERROR_CODES.TOKEN_INVALID:
      case AUTH_CONFIG.ERROR_CODES.NOT_AUTHENTICATED:
      case AUTH_CONFIG.ERROR_CODES.AUTHENTICATION_ERROR:
        // Para otros errores, mostrar error
        this.showError(message);
        break;

      default:
        this.showError('Error de autenticación desconocido');
    }
  }

  /**
   * Muestra un mensaje de error
   */
  private showError(message: string): void {
    console.error('🔴 AUTH ERROR:', message);

    // TODO: Integrar con tu sistema de notificaciones
    // Ejemplo con Angular Material:
    // this.snackBar.open(message, 'Cerrar', {
    //   duration: 5000,
    //   panelClass: ['error-snackbar']
    // });

    // Ejemplo con PrimeNG:
    // this.messageService.add({
    //   severity: 'error',
    //   summary: 'Error de Autenticación',
    //   detail: message,
    //   life: 5000
    // });
  }

  /**
   * Muestra un mensaje informativo
   */
  private showInfo(message: string): void {
    console.info('ℹ️ AUTH INFO:', message);

    // TODO: Integrar con tu sistema de notificaciones
    // Ejemplo con Angular Material:
    // this.snackBar.open(message, 'OK', {
    //   duration: 3000,
    //   panelClass: ['info-snackbar']
    // });

    // Ejemplo con PrimeNG:
    // this.messageService.add({
    //   severity: 'info',
    //   summary: 'Información',
    //   detail: message,
    //   life: 3000
    // });
  }

  /**
   * Muestra un mensaje de éxito
   */
  showSuccess(message: string): void {
    console.log('✅ AUTH SUCCESS:', message);

    // TODO: Integrar con tu sistema de notificaciones
  }

  /**
   * Obtiene el mensaje de error apropiado para cada código
   */
  private getErrorMessage(errorCode: AuthErrorCode): string {
    switch (errorCode) {
      case AUTH_CONFIG.ERROR_CODES.TOKEN_EXPIRED:
        return AUTH_CONFIG.ERROR_MESSAGES.TOKEN_EXPIRED;
      case AUTH_CONFIG.ERROR_CODES.TOKEN_INVALID:
        return AUTH_CONFIG.ERROR_MESSAGES.TOKEN_INVALID;
      case AUTH_CONFIG.ERROR_CODES.NOT_AUTHENTICATED:
        return AUTH_CONFIG.ERROR_MESSAGES.NOT_AUTHENTICATED;
      case AUTH_CONFIG.ERROR_CODES.AUTHENTICATION_ERROR:
        return AUTH_CONFIG.ERROR_MESSAGES.AUTHENTICATION_ERROR;
      default:
        return 'Error de autenticación';
    }
  }
}
